import type { SegmentKey, Side } from '../hs1/model'
import type { Vec3 } from '../hs1/vec'

/**
 * Die Schnittstelle zum PLM — als Vertrag, nicht als Implementierung.
 *
 * WAS AUS DEM PLM ZURÜCKKOMMT UND WAS NICHT. Nach dem Konstruieren liegen im
 * CAD Einzelteile vor. Für die Roboterphysik ist die FORM eines Teils
 * bedeutungslos — kein Rechenschritt in diesem Kern fragt danach, wie ein
 * Träger aussieht. Was zählt, ist seine Lage im Raum, seine Masse, sein
 * Schwerpunkt und sein Trägheitstensor. Genau das steht in diesem Vertrag, und
 * nichts darüber hinaus.
 *
 * Ein Vertrag, der die Geometrie mitführte, wäre die Stelle, an der jemand
 * anfängt, Tessellierungen durch den Rechenkern zu schieben.
 *
 * DIE SCHÄTZUNG WIRD NICHT ERSETZT, SIE BEKOMMT EINE ZWEITE SPALTE. Das Modell
 * trägt heute anthropometrisch abgeleitete Massen. Wenn ein gemessener Wert
 * ankommt, tritt er daneben und nicht an ihre Stelle — die Abweichung ist die
 * eigentliche Auskunft: sie sagt, wie weit die Konstruktion von der Annahme
 * abweicht, auf der alle bisherigen Zahlen beruhen.
 *
 * DIE ANBINDUNG IST HEUTE SIMULIERT. Der Client spricht echtes HTTP gegen eine
 * echte Adresse; dahinter liegt ein Datensatz im Repository statt eines PLM.
 * Wer ein echtes PLM anschliesst, tauscht die Basisadresse und sonst nichts —
 * deshalb steht der Vertrag hier und die Übertragung in `http.ts`.
 */

/** Reifegrad eines Teils im PLM. */
export type PlmState = 'entwurf' | 'in_pruefung' | 'freigegeben'

/**
 * Der Trägheitstensor eines Teils um SEINEN EIGENEN Schwerpunkt, kg·m².
 *
 * Nur die Hauptdiagonale. Die Deviationsmomente fehlen bewusst: das
 * Gangmodell rechnet je Ebene und kennt keine Kreuzkopplung zwischen ihnen.
 * Sie mitzuführen hiesse, eine Genauigkeit zu behaupten, die kein Rechenschritt
 * dahinter einlöst.
 */
export interface Inertia {
  /** Um die Längsachse x — Rollen. */
  xx: number
  /** Um die Querachse y — Nicken. Das ist die Zahl, die im Gangmodell zählt. */
  yy: number
  /** Um die Hochachse z — Gieren. */
  zz: number
}

export interface PlmPart {
  /** Teilenummer — die Kennung im PLM. */
  number: string
  revision: string
  name: string
  /**
   * Zu welchem Segment des Rechenkerns das Teil gehört.
   *
   * Die Zuordnung trifft das PLM und nicht dieser Kern: sie ist eine Aussage
   * über die Konstruktion („dieser Träger ist Teil des Oberschenkels") und
   * keine, die sich aus einer Koordinate ableiten liesse.
   */
  segment: SegmentKey
  /** Seite bei paarigen Segmenten. `null` bei mittigen Bauteilen. */
  side: Side | null
  /** Masse in Kilogramm. */
  mass: number
  /**
   * Schwerpunkt im KONSTRUKTIONSRAHMEN, Millimeter.
   *
   * Derselbe Nullpunkt wie der Punktsatz: das grosse Kreuzgelenk des
   * Rumpfaktuators. Das ist die „Relativität im Raum", auf die es ankommt —
   * eine Masse ohne Ort ist für die Dynamik wertlos.
   */
  cog: Vec3
  inertia: Inertia
  material: string
  state: PlmState
  /**
   * Welche Position der Stückliste dieses Teil traegt — oder `null`.
   *
   * WARUM DAS FELD SCHON DA IST, OBWOHL NICHTS ES FUELLT. Der Vertrag ordnet
   * Teile bisher nur SEGMENTEN zu („dieser Traeger gehoert zum Oberschenkel").
   * Das genuegt fuer Masse, Schwerpunkt und Traegheit — die Stueckliste fragt
   * aber anders: sie fuehrt Positionen (ein Antriebsmodul, eine Leistungsstufe,
   * ein Akkupack), und ein Segment ist keine Position.
   *
   * Ohne dieses Feld laesst sich die Frage „welche Zeile kommt aus dem PLM"
   * nicht beantworten, egal wie vollstaendig das PLM ist. Es steht deshalb
   * jetzt im Vertrag, damit die Gegenstelle weiss, was sie liefern soll, und
   * die Oberflaeche die Spalte zeigen kann, bevor sie gefuellt ist.
   *
   * Die Schluessel sind die der Stueckliste: `a:` fuer Achsen, `f:` fuer feste
   * Positionen. Sie stehen in `bom.ts` und werden dort erzeugt.
   */
  bomKey?: string | null
}

export interface PlmAssembly {
  /** Kennung der Baugruppe im PLM. */
  id: string
  label: string
  revision: string
  /** Freigabedatum, ISO. */
  releasedAt: string
  /**
   * Woher die Daten kommen — im Klartext.
   *
   * Steht in der Oberfläche. Solange hier „simuliert" steht, darf keine Zahl
   * dieser Seite in eine Freigabe.
   */
  source: string
  parts: PlmPart[]
}

/**
 * Was ein Client können muss.
 *
 * Zwei Aufrufe, mehr braucht der Kern nicht: sagen, ob die Gegenstelle da ist,
 * und eine Baugruppe holen. Ein Vertrag mit zwanzig Methoden bindet den Kern an
 * die Eigenheiten eines bestimmten PLM.
 */
export interface PlmClient {
  /** Kurze Auskunft über die Gegenstelle — für die Anzeige, nicht für die Rechnung. */
  info(): Promise<PlmInfo>
  assembly(id: string): Promise<PlmAssembly>
}

/*
  DIE RICHTUNG IST EINE EINBAHNSTRASSE, und das ist Absicht.

  Der Vertrag kennt zwei Aufrufe, und beide LESEN. Es gibt kein `save`, kein
  `update`, kein `release` — diese Anwendung schreibt nichts ins PLM zurueck.

  Der Grund ist nicht Bequemlichkeit. Das PLM ist die fuehrende Quelle fuer
  Geometrie und Masse; was hier gerechnet wird, ist eine Auslegung daneben. Ein
  Rueckschreibweg wuerde aus einer Auslegung stillschweigend einen Konstruktions-
  stand machen — und niemand koennte danach noch sagen, welche Zahl gemessen und
  welche gerechnet war. Wer eine Zahl von hier ins PLM bringen will, tut das von
  Hand und traegt die Entscheidung.
*/

export interface PlmInfo {
  /** Basisadresse, gegen die gesprochen wird. */
  endpoint: string
  /** Ist es ein echtes PLM oder der Datensatz aus dem Repository? */
  simulated: boolean
  /** Welche Baugruppen die Gegenstelle kennt. */
  assemblies: { id: string; label: string; revision: string }[]
}

/** Die Fehler, die ein Client wirft — damit die Oberfläche sie unterscheiden kann. */
export class PlmError extends Error {
  constructor(
    message: string,
    /** `unreachable` — die Gegenstelle antwortet nicht. `invalid` — sie antwortet Unsinn. */
    readonly kind: 'unreachable' | 'notfound' | 'invalid',
  ) {
    super(message)
    this.name = 'PlmError'
  }
}
